import { useCollection } from '@staffingengine/common'
import { type ClientMetaPagination, type UserRole } from '@staffingengine/database-client'
import { type UserProfilesRow } from '@staffingengine/database-types'

export interface UserProfile extends UserProfilesRow {
  team_members: Array<{ id: string }> | null
}

// TODO: export properly from database client
interface UsersFetchAllFilters {
  role?: UserRole
  organization_id?: string[]
}

const DEFAULT_PAGINATION: ClientMetaPagination = {
  current_page: 1,
  page_size: 50,
  total: null,
}

const MAX_PAGE_SIZE = 500

const DEFAULT_FILTERS: UsersFetchAllFilters = {
  role: undefined,
  organization_id: undefined,
}

export const useUserProfilesStore = defineStore('user-profiles', () => {
  const client = useDatabaseClient()
  const auth = useAuth()

  const isLoading = ref(false)
  // If the users array is empty and the api is loading, this is the first load
  const isLoadingInitial = computed(() => !users.value.length && isLoading.value)

  const [users, usersCollection] = useCollection<UserProfile>()
  const meta = ref<ClientMetaPagination | undefined>(undefined)

  const signedInUser = ref<UserProfile | undefined>(undefined)

  watchEffect(() => {
    const userMetadata = auth.userMetadata.value
    const _users = users.value

    if (!userMetadata || !userMetadata.user) return
    if (!_users) return

    signedInUser.value = _users.find((user) => user.id === userMetadata.user)
  })

  //
  // Utils
  //

  const reset = () => {
    usersCollection.empty()
    meta.value = undefined
    pageSize.value = DEFAULT_PAGINATION.page_size
    page.value = DEFAULT_PAGINATION.current_page
    filters.value = DEFAULT_FILTERS
  }

  // ---
  // Fetch (pagination, filtering, and request)
  // ---

  const pagination = computed(() => meta.value || DEFAULT_PAGINATION)

  // Page Size

  const pageSize = ref(DEFAULT_PAGINATION.page_size)
  watch(
    () => pageSize.value,
    async (val, oldVal) => {
      if (val === oldVal) return
      console.log(`page size updated: ${oldVal} -> ${val}`)
      await fetch({ reset: true })
    },
  )

  /**
   * Set the page size for the fetch request.
   *
   * Once set, `fetch()` will automatically run and re-request from the first page.
   *
   * If the supplied page size is invalid (i.e. greater than the max), nothing will happen
   * // TODO: throw error instead of returning undefined?
   */
  const setPageSize = (newPageSize: number) => {
    if (newPageSize > MAX_PAGE_SIZE) return
    pageSize.value = newPageSize
  }

  // Page

  const page = ref(DEFAULT_PAGINATION.current_page)
  watch(
    () => page.value,
    async (val, oldVal) => {
      if (val === oldVal) return
      console.log(`page number updated: ${oldVal} -> ${val}`)
      await fetch()
    },
  )

  /**
   * Set the page for the fetch request.
   *
   * Once set, `fetch()` will automatically request the next page.
   *
   * If the supplied page is invalid (i.e. greater than the total pages available) nothing will happen
   * // TODO: throw error instead of returning undefined?
   */
  const setPage = (newPage: number) => {
    if (!pagination.value.total) return
    if (pagination.value.total > newPage) return

    page.value = newPage
  }

  // Filters

  const filters = ref<UsersFetchAllFilters>(DEFAULT_FILTERS)
  watch(
    () => filters.value,
    async (val, oldVal) => {
      // TODO: might need to deep equal
      if (val === oldVal) return
      console.log(`filters updated: ${oldVal} -> ${val}`)
      await fetch({ reset: true })
    },
  )

  /**
   * Set the filters for the fetch request.
   *
   * Once set, `fetch()` will automatically request the next page.
   *
   * // TODO: throw if invalid?
   *
   * @param newFilters - a valid filters object or undefined. undefined will reset filters to default.
   */
  const setFilters = (newFilters?: UsersFetchAllFilters) => {
    if (!newFilters) {
      filters.value = DEFAULT_FILTERS
      return
    }

    filters.value = newFilters
  }

  /**
   * Fetch with the currently set pagination and filtering
   *
   * @param options
   * @param options.reset - request from page 1
   */
  const fetch = async ({ reset }: { reset?: boolean } = {}) => {
    try {
      isLoading.value = true

      const response = await client.userProfiles.fetchAll({
        pageSize: pageSize.value,
        page: reset ? 1 : page.value,
        filters: filters.value,
      })

      if (response.data) {
        usersCollection.mergeBy('id', response.data as UserProfile[])
      }

      if (response.meta) {
        meta.value = response.meta
      }

      isLoading.value = false
    } catch (err) {
      const error = err as Error
      isLoading.value = false

      throw error
    }
  }

  const fetchById = async (id: string) => {
    try {
      isLoading.value = true

      const response = await client.userProfiles.fetchById(id)

      if (!response.data) {
        isLoading.value = false
        return
      }

      usersCollection.updateBy('id', response.data as UserProfile)

      isLoading.value = false
    } catch (err) {
      const error = err as Error
      isLoading.value = false

      throw error
    }
  }

  return {
    isLoading,
    isLoadingInitial,
    users,
    signedInUser,
    pagination,
    setPageSize,
    setPage,
    setFilters,
    fetch,
    fetchById,
    reset,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserProfilesStore, import.meta.hot))
}
